const CATEGORY_LABELS = {
  all: '전체',
  main: '메인',
  handicap: '핸디캡',
  over_under: '오버·언더',
  half: '전·후반',
  quarter: '쿼터',
  inning_range: '구간이닝',
  inning: '이닝별',
  set: '세트별',
  period: '피리어드',
  corner_card: '코너·카드',
  score: '스코어',
  score_scorer: '스코어·득점자',
  combo: '조합',
  map: '맵별',
  round_objective: '라운드·오브젝트',
  player: '선수',
  other: '기타',
};

const LABEL_TO_KEY = Object.fromEntries(
  Object.entries(CATEGORY_LABELS).map(([key, label]) => [label, key]),
);

const SPORT_TAB_KEYS = {
  soccer: ['all', 'main', 'handicap', 'over_under', 'half', 'corner_card', 'score', 'combo', 'other'],
  basketball: ['all', 'main', 'handicap', 'over_under', 'half', 'quarter', 'player', 'other'],
  baseball: ['all', 'main', 'handicap', 'over_under', 'inning_range', 'inning', 'player', 'other'],
  volleyball: ['all', 'main', 'handicap', 'over_under', 'set', 'score', 'other'],
  ice_hockey: ['all', 'main', 'handicap', 'over_under', 'period', 'score_scorer', 'player', 'other'],
  esports: ['all', 'main', 'handicap', 'over_under', 'map', 'round_objective', 'player', 'other'],
};

const FALLBACK_TAB_KEYS = [
  'all', 'main', 'handicap', 'over_under', 'half', 'quarter', 'inning_range',
  'inning', 'set', 'period', 'corner_card', 'score', 'score_scorer',
  'combo', 'map', 'round_objective', 'player', 'other',
];

const normalizeKey = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9가-힣]+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizeSportKey = (value) => {
  const key = normalizeKey(value).replace(/-/g, '_');
  if (key.includes('ice_hockey') || key.includes('hockey') || key.includes('아이스하키') || key.includes('하키')) return 'ice_hockey';
  if (key.includes('e_sports') || key.includes('esports') || key.includes('이스포츠')) return 'esports';
  if (key.includes('soccer') || key.includes('football') || key.includes('축구')) return 'soccer';
  if (key.includes('basketball') || key.includes('농구')) return 'basketball';
  if (key.includes('baseball') || key.includes('야구')) return 'baseball';
  if (key.includes('volleyball') || key.includes('배구')) return 'volleyball';
  return key;
};

export const normalizeSportsnewMarketCategoryKey = (value) => {
  if (value == null) return 'all';
  const raw = String(value).trim();
  if (LABEL_TO_KEY[raw]) return LABEL_TO_KEY[raw];
  const key = normalizeKey(raw);
  return LABEL_TO_KEY[key] ?? key;
};

const tabFromKey = (key, count = 0, order = null) => ({
  id: key,
  key,
  label: CATEGORY_LABELS[key] ?? key,
  count: Number.isFinite(Number(count)) ? Number(count) : 0,
  order: Number.isFinite(Number(order)) ? Number(order) : FALLBACK_TAB_KEYS.indexOf(key) * 10,
});

const normalizeApiTab = (tab, index = 0) => {
  const key = normalizeSportsnewMarketCategoryKey(tab?.key ?? tab?.id ?? tab?.categoryKey ?? tab?.value);
  return tabFromKey(
    key,
    tab?.count,
    tab?.order ?? index * 10,
  );
};

const marketCategoryKeys = (market) => {
  const keys = Array.isArray(market?.displayCategoryKeys)
    ? market.displayCategoryKeys
    : [];
  const primary = market?.displayPrimaryCategoryKey;
  return [...new Set([
    ...keys,
    ...(primary ? [primary] : []),
  ].map(normalizeSportsnewMarketCategoryKey).filter(Boolean))];
};

export const buildSportsnewMarketTabs = ({ fixture = null, markets = [], sport = null } = {}) => {
  const apiTabs = fixture?.marketCategories
    ?? fixture?.categories
    ?? fixture?.displayCategories
    ?? fixture?.marketCategoryTabs
    ?? [];
  if (Array.isArray(apiTabs) && apiTabs.length > 0) {
    return apiTabs
      .map(normalizeApiTab)
      .filter((tab) => tab.key && CATEGORY_LABELS[tab.key])
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ko'));
  }

  const sportKey = normalizeSportKey(sport ?? fixture?.sport ?? fixture?.sportSlug ?? fixture?.sport_slug);
  const fallbackKeys = SPORT_TAB_KEYS[sportKey];
  if (fallbackKeys) return fallbackKeys.map((key, index) => tabFromKey(key, 0, index * 10));

  const discovered = new Map([['all', tabFromKey('all', Array.isArray(markets) ? markets.length : 0, 0)]]);
  (Array.isArray(markets) ? markets : []).forEach((market) => {
    marketCategoryKeys(market).forEach((key) => {
      if (!CATEGORY_LABELS[key]) return;
      const current = discovered.get(key) ?? tabFromKey(key, 0, market?.displayCategoryOrder);
      discovered.set(key, { ...current, count: current.count + 1 });
    });
  });

  return [...discovered.values()]
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ko'));
};

export const marketMatchesSportsnewCategory = (market, activeCategoryKey) => {
  const key = normalizeSportsnewMarketCategoryKey(activeCategoryKey);
  if (!key || key === 'all') return true;

  const keys = marketCategoryKeys(market);
  if (keys.length === 0) return true;
  return keys.includes(key);
};

export const ensureSportsnewMarketTab = (tabs, activeCategoryKey) => {
  const key = normalizeSportsnewMarketCategoryKey(activeCategoryKey);
  return (tabs ?? []).some((tab) => tab.id === key) ? key : 'all';
};
